import React, { useMemo } from "react";
import {
  formatTournamentDate,
  payChipClass,
  resultBadgeClass,
  timelineCellValue,
  tournamentStatusBadgeClass,
} from "../../utils/myTournamentsDirectoryHelpers";

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "upcoming") return "upcoming";
  if (s === "ongoing") return "ongoing";
  if (s === "completed") return "completed";
  return "muted";
}

function resultTone(result) {
  const s = String(result || "").toLowerCase();
  if (s.includes("winner")) return "winner";
  if (s.includes("runner")) return "runner";
  if (s === "pending" || !s) return "pending";
  return "default";
}

function TimelineSkeleton() {
  return (
    <div className="mt-timeline mt-timeline--loading" aria-hidden>
      {[1, 2, 3].map((n) => (
        <div key={n} className="mt-timeline__skeleton-item">
          <span className="mt-timeline__skeleton-node" />
          <span className="mt-timeline__skeleton-card" />
        </div>
      ))}
    </div>
  );
}

function TimelineCard({ row }) {
  const status = row.status || "—";
  const result = row.result || "Pending";
  const paymentStatus = timelineCellValue("payment_status", row);
  const amount = timelineCellValue("payment_amount", row);
  const tone = statusTone(status);
  const resultKey = resultTone(result);

  return (
    <article
      className={`mt-timeline__card mt-timeline__card--${tone} mt-timeline__card--result-${resultKey}`}
    >
      <header className="mt-timeline__card-head">
        <div className="mt-timeline__card-title-wrap">
          <h4 className="mt-timeline__card-title">
            {row.tournament || row.tournament_name || "Tournament"}
          </h4>
          <span className="mt-timeline__card-code">{row.tournament_id || "—"}</span>
        </div>
        <time className="mt-timeline__card-date" dateTime={row.tournament_date || undefined}>
          {formatTournamentDate(row.tournament_date)}
        </time>
      </header>

      <div className="mt-timeline__card-meta">
        <span className="mt-timeline__meta-tag">{row.level || row.tournament_level || "—"}</span>
        <span className="mt-timeline__meta-dot" aria-hidden>
          ·
        </span>
        <span className="mt-timeline__meta-tag">{row.sport_type || "—"}</span>
        {row.location && row.location !== "—" ? (
          <>
            <span className="mt-timeline__meta-dot" aria-hidden>
              ·
            </span>
            <span className="mt-timeline__meta-tag mt-timeline__meta-tag--venue">
              {row.location}
            </span>
          </>
        ) : null}
      </div>

      <footer className="mt-timeline__card-foot">
        <span className={tournamentStatusBadgeClass(status)}>{status}</span>
        <span className={resultBadgeClass(result)}>{result}</span>
        <span className={payChipClass(paymentStatus)}>{paymentStatus}</span>
        {amount && amount !== "—" ? (
          <span className="mt-timeline__amount">{amount}</span>
        ) : null}
      </footer>
    </article>
  );
}

export default function MyTournamentsTimelineSection({ timeline, loading }) {
  const rows = useMemo(
    () => (Array.isArray(timeline) ? timeline : []),
    [timeline]
  );

  if (loading) {
    return (
      <section className="user-builder-panel mt-timeline-panel" aria-label="Participation timeline">
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Participation timeline</h3>
          <p className="user-builder-panel__hint">
            Your tournament journey in chronological order — newest first.
          </p>
        </div>
        <TimelineSkeleton />
      </section>
    );
  }

  if (rows.length === 0) {
    return (
      <section className="user-builder-panel mt-timeline-panel" aria-label="Participation timeline">
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Participation timeline</h3>
          <p className="user-builder-panel__hint">
            Complete history of your tournament participations.
          </p>
        </div>
        <p className="mm-empty">No participation history available.</p>
      </section>
    );
  }

  return (
    <section className="user-builder-panel mt-timeline-panel" aria-label="Participation timeline">
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Participation timeline</h3>
        <p className="user-builder-panel__hint">
          Your tournament journey in chronological order — newest first.
        </p>
      </div>

      <ol className="mt-timeline" aria-label="Tournament participation history">
        {rows.map((row, index) => {
          const year = row.year || "—";
          const prevYear = index > 0 ? rows[index - 1].year : null;
          const showYear = year !== prevYear;
          const tone = statusTone(row.status);

          return (
            <li
              key={`timeline-${row.id}`}
              className={`mt-timeline__item mt-timeline__item--${tone}`}
            >
              {showYear ? (
                <div className="mt-timeline__year" aria-label={`Year ${year}`}>
                  <span className="mt-timeline__year-label">{year}</span>
                </div>
              ) : null}

              <div className="mt-timeline__body">
                <div className="mt-timeline__track">
                  <span className="mt-timeline__node" aria-hidden="true">
                    <span className="mt-timeline__node-inner" />
                  </span>
                  {index < rows.length - 1 ? (
                    <span className="mt-timeline__connector" aria-hidden="true" />
                  ) : null}
                </div>

                <TimelineCard row={row} />
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
